/**
 * Persists mobile path (trunk) and expanded branches per source + language
 * so a full reload does not lose tree context.
 */

import { TreeUtils } from './tree-utils.js';
import { areArboritoStorageWritesDisabled } from '../../../shared/lib/arborito-storage-gate.js';

const STORAGE_VERSION = 1;
const STORAGE_PREFIX = 'arborito-tree-ui:';

function storageKey(source, lang) {
    const sid = String(source?.id || source?.url || source?.name || 'default');
    return `${STORAGE_PREFIX}${sid}:${String(lang || 'EN')}`;
}

function collectExpandedIds(root) {
    const ids = [];
    const walk = (node) => {
        if (!node) return;
        if ((node.type === 'branch' || node.type === 'root') && node.expanded) {
            ids.push(String(node.id));
        }
        if (node.children) node.children.forEach(walk);
    };
    walk(root);
    return ids;
}

function applyExpandedSnapshot(root, expandedSet) {
    const walk = (node) => {
        if (!node) return;
        if (node.type === 'branch' || node.type === 'root') {
            node.expanded = expandedSet.has(String(node.id));
        }
        if (node.children) node.children.forEach(walk);
    };
    walk(root);
}

function validateMobilePath(root, ids) {
    if (!root) return [];
    const want = Array.isArray(ids) ? ids.map(String) : [];
    if (want.length === 0 || String(want[0]) !== String(root.id)) {
        return [String(root.id)];
    }
    const out = [String(root.id)];
    let cur = root;
    for (let i = 1; i < want.length; i++) {
        const id = want[i];
        const next = TreeUtils.resolvePathChild(cur, id, (tid) => TreeUtils.findNode(tid, root));
        if (!next) break;
        out.push(id);
        cur = next;
    }
    return out;
}

/** @param {EventTarget & { state: { data: object, activeSource: object, lang: string } }} store */
export function persistTreeUiState(store) {
    if (areArboritoStorageWritesDisabled()) return;
    const root = store.state.data;
    const source = store.state.activeSource;
    if (!root || !source) return;
    try {
        const fromStore = store.state.graphUi?.mobilePath;
        const mobilePath =
            Array.isArray(fromStore) && fromStore.length > 0
                ? fromStore.map(String)
                : [String(root.id)];
        const expandedIds = collectExpandedIds(root);
        const payload = {
            v: STORAGE_VERSION,
            mobilePath,
            expandedIds,
            treeSig: TreeUtils.treeStructureSig(root),
        };
        localStorage.setItem(storageKey(source, store.state.lang), JSON.stringify(payload));
    } catch (e) {
        console.warn('tree-ui-persist: save failed', e);
    }
}

let _debounceTimer = null;
let _lastPersistSig = '';

export function cancelScheduledPersistTreeUiState() {
    if (_debounceTimer != null) {
        clearTimeout(_debounceTimer);
        _debounceTimer = null;
    }
}

/** @param {EventTarget & { state: { data: object, activeSource: object, lang: string } }} store */
export function schedulePersistTreeUiState(store) {
    if (areArboritoStorageWritesDisabled()) return;
    if (_debounceTimer != null) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => {
        _debounceTimer = null;
        if (areArboritoStorageWritesDisabled()) return;
        const root = store.state.data;
        const source = store.state.activeSource;
        if (!root || !source) return;

        const fromStore = store.state.graphUi?.mobilePath;
        const mobilePath =
            Array.isArray(fromStore) && fromStore.length > 0
                ? fromStore.map(String)
                : [String(root.id)];
        const sig = `${source.id || source.url}|${store.state.lang}|${mobilePath.join('>')}|${TreeUtils.treeStructureSig(root)}`;
        if (sig === _lastPersistSig) return;
        _lastPersistSig = sig;

        try {
            const key = storageKey(source, store.state.lang);
            const expandedIds = collectExpandedIds(root);
            const payload = {
                v: STORAGE_VERSION,
                mobilePath,
                expandedIds,
                treeSig: TreeUtils.treeStructureSig(root),
            };
            localStorage.setItem(key, JSON.stringify(payload));
        } catch (e) {
            console.warn('tree-ui-persist: save failed', e);
        }

        if (typeof store.syncTreeContextFromMobilePath === 'function') {
            store.syncTreeContextFromMobilePath();
        }
    }, 500);
}

/** @param {EventTarget & { state: { data: object, activeSource: object, lang: string } }} store */
export function restoreTreeUiStateAfterLoad(store) {
    const root = store.state.data;
    const source = store.state.activeSource;
    if (!root || !source) return;

    let parsed = null;
    try {
        const raw = localStorage.getItem(storageKey(source, store.state.lang));
        if (raw) parsed = JSON.parse(raw);
    } catch {
        return;
    }
    if (!parsed || parsed.v !== STORAGE_VERSION) return;

    const currentSig = TreeUtils.treeStructureSig(root);
    if (parsed.treeSig && parsed.treeSig !== currentSig) {
        return;
    }

    if (Array.isArray(parsed.expandedIds) && parsed.expandedIds.length > 0) {
        applyExpandedSnapshot(root, new Set(parsed.expandedIds.map(String)));
    }

    if (Array.isArray(parsed.mobilePath) && parsed.mobilePath.length > 0) {
        const mobilePath = validateMobilePath(root, parsed.mobilePath);
        store.dispatchEvent(new CustomEvent('arborito-set-mobile-path', { detail: { ids: mobilePath } }));
    }
}
